import { Dayjs } from 'dayjs'
import {
  MasterData,
  MasterDataWithCategory,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import { ExchangeByValue } from 'src/utils/types'
import { DecodedEmailSettings } from '../../api/types/currentCompany'
import { Specialty } from '../../api/types/specialty'
import { PromotionParam } from '../Promotion'

type MatchingStatus =
  | 'request'
  | 'not_interest'
  | 'matched'
  | 'sended'
  | 'be_rejected'
  | 'matching'
  | null

export type UserSpecialties = {
  id: number
  name: string
  fieldType: string
  createdAt: string
  updatedAt: string
}

export type LoginUser = {
  id: number
  profile: LoginUserProfile
  company: CompanyData
  responsibilities: Responsibilities
  needs: Needs
  solutions: Solutions
  personas: Personas
  plan: Plan
  settings: {
    facebookLogin: boolean
    showMessageAttention: boolean
  }
  showFeedbackModal: boolean
  showAppointmentModal: boolean
  loggedIn: boolean
  specialties: any[]
  notice: {
    chatworkNoticeFlag: boolean
    slackNoticeFlag: boolean
    smsNoticeFlag: boolean
    emailNoticeFlag: boolean
    messageAvailable: boolean
    receiveAllEmailSetting?: DecodedEmailSettings
    chatworkApiToken?: string
    chatworkRoomId?: string
    slackApiToken?: string
  }
  member: Member
  isAgreedPolicy: boolean
  withInSalesDemoPlanPeriod: boolean
  salesDemoDaysLeftText: number
}

export type Member = {
  bonusGetAt: string | null
  companyId: number
  createdAt: string | null
  flagDate: string | null
  id: number
  loginBonusCount: number
  loginBonusFlag: boolean
  messageCount: number
  messageTotal: number | null
  updatedAt: string | null
  showMessageCountSideMessage: number
}

type AsMasterDataLabel<T> = ExchangeByValue<
  ExchangeByValue<T, MasterData | undefined, string | null>,
  MasterData[],
  string[]
>

type CompanyDataForUser = Omit<
  AsMasterDataLabel<CompanyData>,
  'businessType'
> & {
  businessType?: string | null
}

export type User = {
  id: number
  profile: AsMasterDataLabel<UserProfile>
  company: CompanyDataForUser
  responsibilities: AsMasterDataLabel<Responsibilities>
  needs: AsMasterDataLabel<Needs>
  solutions: AsMasterDataLabel<Solutions>
  personas: {
    items: AsMasterDataLabel<Persona>[]
  }
  specialties: AsMasterDataLabel<Specialties>
  kept?: boolean
  isReferrer: boolean
}

export type Plan = {
  free: boolean
  paid: boolean
  salesDemo: boolean
  freeAndPartner: boolean
  role: RoleType | ''
  isReferrer: boolean
  isAdminRole: boolean
  freeTrialEndAt?: string | null
}

export const ROLE = {
  FREE: 'free',
  RECESS: 'recess',
  FREE_TRIAL: 'free_trial',
  PARTNER: 'partner',
  LIGHT: 'light',
  LIGHT_OTHER: 'light_other',
  STANDARD: 'standard',
  PREMIUM: 'premium',
  SALES_DEMO: 'sales_demo',
  ONLYSTORY_REFERRER: 'onlystory_referrer',
  DEMO: 'demo',
  SENDING_AGENCY: 'sending_agency',
  BATCH_SENDING: 'batch_sending',
} as const
export type RoleType = typeof ROLE[keyof typeof ROLE]

export const CREATE_STATUS = {
  TEMPORARY: 'temporary',
  APPLY: 'apply',
  FIRST_SEMINAR: 'first_seminar',
  APPROVAL: 'approval',
  LOGIN: 'login',
  NON_APPROVAL: 'non_approval',
  REQUESTED_DELETION: 'requested_deletion',
} as const
export type CreateStatusType = typeof CREATE_STATUS[keyof typeof CREATE_STATUS]

export type LoginUserProfile = UserProfile & {
  nameRuby?: string
  email: string
  emailCc?: string[]
  phoneNumber?: string
  birthYear?: string
}
export type UserProfile = {
  name: string
  iconSrc: string
  fbUrl?: string
  position?: MasterData
  isCertified?: boolean
}

export type CompanyData = {
  name: string
  nameRuby?: string
  numberOfEmployees?: MasterData
  businessType?:
    | MasterData
    | MasterDataWithCategory
    | (MasterData | MasterDataWithCategory)[]
  capital?: MasterData
  prefecture?: MasterDataWithRegionalDivisionType
  foundedOn?: string
  old?: number
  businessObject?: MasterData
  serviceDescription?: string
  isListed?: boolean | null
  storyUrl?: string
  hpUrl?: string
  fbUrl?: string
  scheduleUrl?: string
  isActiveScheduleUrl?: boolean
  scheduleUrlBannerLastClosedAt?: Dayjs
  canCreatePersona?: boolean
  paidPlan?: boolean
  roomUrl?: string | null
  messageAvailable?: boolean
  adminAges?: string
  createStatus?: CreateStatusType
  matchingStatus?: MatchingStatus
  isMatch?: boolean
  feedbackScore?: number
  feedbackCount?: number
  /** プロフィール詳細 API の connection_count（つながり人数の正） */
  connectionCount?: number
  connectionCompanies?: ConnectionCompany[]
  introductoryTextCompanies?: IntroductoryTextCompany[]
}

type ConnectionCompany = {
  id: number
  name: string
  adminName: string
  imageUrl: string
  matchingStatus: MatchingStatus
}

type IntroductoryTextCompany = {
  id: number
  name: string
  adminName: string
  imageUrl: string
  content: string
  createdAt: Dayjs
}

export type FirstSettingProfile = {
  adminName: string
  email: string
  birthYear: string
  position?: MasterData
  image: string

  name: string
  businessType?:
    | MasterData
    | MasterDataWithCategory
    | (MasterData | MasterDataWithCategory)[]
  businessObject?: MasterData
  numberOfEmployees?: MasterData
  foundedOn: string
  prefecture?: MasterDataWithRegionalDivisionType
  hpUrl: string
  serviceDescription: string
}

export type Responsibilities = {
  domains: MasterData[]
  features: MasterData[]
  onlineMtgAvailable?: boolean | null
  message?: string | null
}

export type Needs = {
  items: MasterDataWithCategory[]
}
export type Solutions = {
  items: MasterDataWithCategory[]
}
export type Personas = {
  items: Persona[]
}
export type Persona = {
  needs: MasterData[]
  numbersOfEnployees: MasterData[]
}
export type Specialties = {
  items: MasterData[]
}

export type RecommendedUser = {
  id: number
  canInterst: boolean
  isActive: boolean
  isNew: boolean
  isCertified: boolean
  name: string
  companyName: string
  imageSrc?: string
  position?: string | null
  businessType?: string | null
  numberOfEmployees?: string | null
  capital?: string | null
  prefecture?: string | null
  foundedOn?: string
  matchingStatus?: MatchingStatus
  roomUrl?: string | null
  promotion?: PromotionParam
  specialties: Specialty[]
  adminAges: string
  typeOfBusiness?: string | string[] | null
  feedbackScore?: number
  feedbackCount?: number
}
export type NewUser = {
  id: number
  roomUrl: string
  isActive: boolean
  isNew: boolean
  isCertified: boolean
  name: string
  companyName: string
  adminAges: string
  imageSrc?: string
  position?: string | null
  businessType?: string | null
  numberOfEmployees?: string | null
  capital?: string | null
  prefecture?: string | null
  foundedOn?: string
  typeOfBusiness?: string | string[] | null
}

export type RequestedUser = {
  id: number
  name: string
  companyName: string
  position: string
  imageSrc: string
  requestAt: Dayjs
  requestReasonText: string
  roomUrl: string
  feedbackScore: number
  feedbackCount: number
}

export type KeepUser = {
  id: number
  roomUrl: string
  isActive: boolean
  isNew: boolean
  isCertified: boolean
  name: string
  companyName: string
  imageSrc?: string
  position?: string | null
  businessType?: string | null
  numberOfEmployees?: string | null
  capital?: string | null
  prefecture?: string | null
  foundedOn?: string
  storyUrl?: string | null
  hpUrl?: string | null
  fbUrl?: string | null
  kept: boolean
  messageAvailable: boolean
  adminAges: string
}

export type KeepUsers = {
  items: KeepUser[]
  totalCount: number
  totalPages: number
  onePageDisplayItems: number
}

export type NeedsUser = {
  id: number
  roomUrl: string
  isActive: boolean
  isNew: boolean
  isCertified: boolean
  name: string
  companyName: string
  imageSrc?: string
  position?: string | null
  businessType?: string | null
  businessTypes?: string[]
  numberOfEmployees?: string | null
  capital?: string | null
  prefecture?: string | null
  foundedOn?: string
  storyUrl?: string | null
  hpUrl?: string | null
  fbUrl?: string | null
  kept: boolean
  messageAvailable: boolean
  adminAges: string
  matchingStatus?: MatchingStatus
  feedbackScore: number
  feedbackCount: number
}

export type NeedsUsers = {
  items: NeedsUser[]
  totalCount: number
  totalPages: number
  onePageDisplayItems: number
}

export const toPreview = ({
  id,
  profile,
  company,
  responsibilities,
  needs,
  solutions,
  personas,
  specialties,
  plan,
}: LoginUser): User => ({
  id: id,
  isReferrer: plan.role === ROLE.ONLYSTORY_REFERRER,
  profile: {
    ...profile,
    position: profile.position?.label,
    isCertified: profile.isCertified,
  },
  company: {
    ...company,
    businessObject: company.businessObject?.label,
    businessType: Array.isArray(company.businessType)
      ? company.businessType.map((item) => item.label).join('、')
      : company.businessType?.label || null,
    numberOfEmployees: company.numberOfEmployees?.label,
    capital: company.capital?.label,
    prefecture: company.prefecture?.label,
  } as CompanyDataForUser,
  responsibilities: {
    ...responsibilities,
    domains: responsibilities.domains.map(({ label }) => label),
    features: responsibilities.features.map(({ label }) => label),
  },
  needs: {
    items: needs.items.map(({ label }) => label),
  },
  solutions: {
    items: solutions.items.map(({ label }) => label),
  },
  personas: {
    items: personas.items.map((persona) => ({
      needs: persona.needs.map(({ label }) => label),
      numbersOfEnployees: persona.numbersOfEnployees.map(({ label }) => label),
    })),
  },
  specialties: {
    items: specialties.map((s) => s.name),
  },
})

export const EMPTY_LOGIN_USER: LoginUser = {
  id: 0,
  loggedIn: false,
  plan: {
    free: false,
    paid: false,
    salesDemo: false,
    freeAndPartner: false,
    role: '',
    isReferrer: false,
    isAdminRole: false,
  },
  settings: {
    facebookLogin: false,
    showMessageAttention: false,
  },
  showAppointmentModal: false,
  showFeedbackModal: false,
  profile: {
    name: '',
    iconSrc: '',
    email: '',
    birthYear: '',
  },
  company: {
    name: '',
  },
  responsibilities: {
    domains: [],
    features: [],
  },
  needs: {
    items: [],
  },
  solutions: {
    items: [],
  },
  personas: {
    items: [],
  },
  specialties: [],
  notice: {
    chatworkNoticeFlag: false,
    slackNoticeFlag: false,
    smsNoticeFlag: false,
    emailNoticeFlag: true,
    messageAvailable: false,
    receiveAllEmailSetting: undefined,
  },
  member: {
    bonusGetAt: null,
    companyId: 0,
    createdAt: null,
    flagDate: null,
    id: 0,
    loginBonusCount: 0,
    loginBonusFlag: false,
    messageCount: 0,
    messageTotal: null,
    updatedAt: null,
    showMessageCountSideMessage: 0,
  },
  isAgreedPolicy: true,
  withInSalesDemoPlanPeriod: false,
  salesDemoDaysLeftText: 0,
}

export const EMPTY_USER: User = {
  id: 0,
  isReferrer: false,
  profile: {
    name: '',
    iconSrc: '',
  },
  company: {
    name: '',
    businessType: null,
  },
  responsibilities: {
    domains: [],
    features: [],
  },
  needs: {
    items: [],
  },
  solutions: {
    items: [],
  },
  personas: {
    items: [],
  },
  specialties: {
    items: [],
  },
}

export const isPaidForMemo = (role: RoleType | ''): boolean => {
  switch (role) {
    case ROLE.FREE_TRIAL:
    case ROLE.LIGHT:
    case ROLE.LIGHT_OTHER:
    case ROLE.STANDARD:
    case ROLE.PREMIUM:
    case ROLE.ONLYSTORY_REFERRER:
    case ROLE.DEMO:
    case ROLE.SALES_DEMO:
      return true
    default:
      return false
  }
}

export const isPaidForFilter = (role: RoleType | ''): boolean => {
  switch (role) {
    case ROLE.LIGHT:
    case ROLE.LIGHT_OTHER:
    case ROLE.STANDARD:
    case ROLE.PREMIUM:
    case ROLE.ONLYSTORY_REFERRER:
    case ROLE.DEMO:
    case ROLE.SALES_DEMO:
      return true
    default:
      return false
  }
}
